import React, { useState, useEffect } from 'react';
import { Plus, XCircle, BarChart2, Clock, X, Save, AlertCircle, CheckCircle } from 'lucide-react';
import { API_BASE_URL } from '../../config';
import { formatDateMoscow } from '../../utils/dateUtils';
import CreateBroadcastForm from './CreateBroadcastForm';

const statusLabels = {
    draft: 'Черновик',
    scheduled: 'Запланирована',
    running: 'Запущена',
    completed: 'Завершена',
    cancelled: 'Отменена',
};

const statusClass = {
    draft: 'bg-slate-100 text-slate-700',
    scheduled: 'bg-amber-100 text-amber-800',
    running: 'bg-blue-100 text-blue-800',
    completed: 'bg-green-100 text-green-800',
    cancelled: 'bg-red-100 text-red-800',
};

const BroadcastsPage = ({ onShowToast }) => {
    const [list, setList] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showCreateForm, setShowCreateForm] = useState(false);
    const [statsCache, setStatsCache] = useState({});
    const [detailId, setDetailId] = useState(null);
    const [detailCampaign, setDetailCampaign] = useState(null);
    const [editMessageText, setEditMessageText] = useState('');
    const [savingText, setSavingText] = useState(false);

    const fetchList = async () => {
        try {
            setLoading(true);
            const r = await fetch(`${API_BASE_URL}/api/broadcasts/`, {
                credentials: 'include',
                headers: { 'Content-Type': 'application/json' },
            });
            if (!r.ok) throw new Error('Failed to fetch');
            const data = await r.json();
            setList(Array.isArray(data) ? data : []);
        } catch (e) {
            console.error(e);
            if (onShowToast) onShowToast('Не удалось загрузить рассылки', 'error');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchList();
    }, []);

    const fetchStats = async (id) => {
        try {
            const r = await fetch(`${API_BASE_URL}/api/broadcasts/${id}/stats`, {
                credentials: 'include',
                headers: { 'Content-Type': 'application/json' },
            });
            if (!r.ok) return;
            const s = await r.json();
            setStatsCache(prev => ({ ...prev, [id]: s }));
            return s;
        } catch (_) {}
    };

    const openDetail = async (id) => {
        setDetailId(id);
        setDetailCampaign(null);
        try {
            const [campRes, statsRes] = await Promise.all([
                fetch(`${API_BASE_URL}/api/broadcasts/${id}`, { credentials: 'include', headers: { 'Content-Type': 'application/json' } }),
                fetch(`${API_BASE_URL}/api/broadcasts/${id}/stats`, { credentials: 'include', headers: { 'Content-Type': 'application/json' } }),
            ]);
            if (campRes.ok) {
                const camp = await campRes.json();
                setDetailCampaign(camp);
                setEditMessageText(camp.message_text || '');
            }
            if (statsRes.ok) {
                const s = await statsRes.json();
                setStatsCache(prev => ({ ...prev, [id]: s }));
            }
        } catch (e) {
            console.error(e);
            if (onShowToast) onShowToast('Не удалось загрузить данные', 'error');
        }
    };

    const closeDetail = () => {
        setDetailId(null);
        setDetailCampaign(null);
    };

    const saveMessageText = async () => {
        if (detailId == null || !detailCampaign) return;
        if (detailCampaign.status !== 'draft') return;
        setSavingText(true);
        try {
            const r = await fetch(`${API_BASE_URL}/api/broadcasts/${detailId}`, {
                method: 'PATCH',
                credentials: 'include',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ message_text: editMessageText.trim() }),
            });
            if (!r.ok) throw new Error('Ошибка сохранения');
            const updated = await r.json();
            setDetailCampaign(updated);
            setEditMessageText(updated.message_text || '');
            fetchList();
            if (onShowToast) onShowToast('Текст рассылки обновлён');
        } catch (e) {
            if (onShowToast) onShowToast('Не удалось сохранить', 'error');
        } finally {
            setSavingText(false);
        }
    };

    const handleCancel = async (id) => {
        if (!window.confirm('Отменить рассылку?')) return;
        try {
            const r = await fetch(`${API_BASE_URL}/api/broadcasts/${id}/cancel`, {
                method: 'POST',
                credentials: 'include',
                headers: { 'Content-Type': 'application/json' },
            });
            if (!r.ok) throw new Error('Cancel failed');
            if (onShowToast) onShowToast('Рассылка отменена');
            fetchList();
        } catch (e) {
            if (onShowToast) onShowToast('Ошибка отмены', 'error');
        }
    };

    const handleScheduleIn = async (id, minutes) => {
        try {
            const r = await fetch(`${API_BASE_URL}/api/broadcasts/${id}/schedule-in?minutes=${minutes}`, {
                method: 'POST',
                credentials: 'include',
                headers: { 'Content-Type': 'application/json' },
            });
            if (!r.ok) throw new Error('Schedule failed');
            if (onShowToast) onShowToast(`Рассылка запланирована через ${minutes} мин`);
            fetchList();
        } catch (e) {
            if (onShowToast) onShowToast('Ошибка планирования', 'error');
        }
    };

    const formatDate = (d) => formatDateMoscow(d);

    return (
        <div className="space-y-6">
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
                <h2 className="text-2xl font-bold text-slate-800">Рассылки</h2>
                <button
                    onClick={() => setShowCreateForm(true)}
                    className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"
                >
                    <Plus size={20} />
                    Создать рассылку
                </button>
            </div>

            {showCreateForm && (
                <CreateBroadcastForm
                    onClose={() => setShowCreateForm(false)}
                    onSuccess={() => {
                        setShowCreateForm(false);
                        fetchList();
                        if (onShowToast) onShowToast('Рассылка создана');
                    }}
                />
            )}

            <p className="text-slate-600 text-sm">
                Технология очередей: сообщения отправляются с задержкой между получателями, чтобы снизить риск блокировки бота.
            </p>

            {loading ? (
                <div className="text-slate-500 py-8">Загрузка...</div>
            ) : (
                <div className="bg-white rounded-xl shadow-sm border border-slate-100 overflow-hidden">
                    <table className="min-w-full divide-y divide-slate-200">
                        <thead className="bg-slate-50">
                            <tr>
                                <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase">ID</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase">Название / Текст</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase">Статус</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase">Статистика</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase">Дата / План</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase">Действия</th>
                            </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-slate-200">
                            {list.map((b) => (
                                <tr key={b.id} className="hover:bg-slate-50">
                                    <td className="px-6 py-4 text-sm font-medium text-indigo-600">{b.id}</td>
                                    <td className="px-6 py-4 text-sm text-slate-700 max-w-xs truncate">
                                        {b.name || b.message_text}
                                    </td>
                                    <td className="px-6 py-4">
                                        <span className={`px-2 py-1 text-xs font-medium rounded-full ${statusClass[b.status] || 'bg-gray-100'}`}>
                                            {statusLabels[b.status] || b.status}
                                        </span>
                                    </td>
                                    <td className="px-6 py-4 text-sm text-slate-600">
                                        <span className="text-green-600">{b.sent_count ?? 0}</span> доставлено
                                        {b.total_recipients != null && (
                                            <> / <span>{b.total_recipients}</span> всего</>
                                        )}
                                        {b.failed_count > 0 && (
                                            <span className="text-red-600 ml-1">, {b.failed_count} ошибок</span>
                                        )}
                                    </td>
                                    <td className="px-6 py-4 text-sm text-slate-500">
                                        {b.scheduled_at && (
                                            <span title="Запланировано">
                                                <Clock size={14} className="inline mr-1" />
                                                {formatDate(b.scheduled_at)}
                                            </span>
                                        )}
                                        {b.completed_at && (
                                            <span className="block text-xs">Завершена: {formatDate(b.completed_at)}</span>
                                        )}
                                        {!b.scheduled_at && !b.completed_at && formatDate(b.created_at)}
                                    </td>
                                    <td className="px-6 py-4 text-sm">
                                        {b.status === 'draft' && (
                                            <>
                                                <button
                                                    onClick={() => handleScheduleIn(b.id, 3)}
                                                    className="text-amber-600 hover:underline mr-2"
                                                >
                                                    Через 3 мин
                                                </button>
                                                <button
                                                    onClick={() => handleScheduleIn(b.id, 10)}
                                                    className="text-amber-600 hover:underline mr-2"
                                                >
                                                    Через 10 мин
                                                </button>
                                            </>
                                        )}
                                        {(b.status === 'scheduled' || b.status === 'running') && (
                                            <button
                                                onClick={() => handleCancel(b.id)}
                                                className="text-red-600 hover:underline flex items-center gap-1"
                                            >
                                                <XCircle size={16} /> Отменить
                                            </button>
                                        )}
                                        {(b.status === 'running' || b.status === 'completed' || b.status === 'draft') && (
                                            <button
                                                onClick={() => openDetail(b.id)}
                                                className="text-indigo-600 hover:underline flex items-center gap-1"
                                            >
                                                <BarChart2 size={16} /> Подробнее / Ошибки
                                            </button>
                                        )}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                    {list.length === 0 && (
                        <div className="p-8 text-center text-slate-500">Нет рассылок. Создайте первую.</div>
                    )}
                </div>
            )}

            {/* Модальное окно: текст рассылки, ошибки, доставлено */}
            {detailId != null && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 overflow-y-auto">
                    <div className="bg-white rounded-xl shadow-lg max-w-3xl w-full max-h-[90vh] overflow-y-auto">
                        <div className="p-6 border-b border-slate-200 flex justify-between items-center sticky top-0 bg-white">
                            <h3 className="text-xl font-bold text-slate-800">
                                Рассылка #{detailId}
                                {detailCampaign?.name && <span className="text-slate-500 font-normal ml-2">— {detailCampaign.name}</span>}
                            </h3>
                            <button onClick={closeDetail} className="text-slate-400 hover:text-slate-600 p-1">
                                <X size={24} />
                            </button>
                        </div>
                        <div className="p-6 space-y-6">
                            {/* Текст рассылки */}
                            <div>
                                <label className="block text-sm font-medium text-slate-700 mb-1">Текст рассылки (что отправляется)</label>
                                <textarea
                                    value={editMessageText}
                                    onChange={(e) => setEditMessageText(e.target.value)}
                                    readOnly={detailCampaign?.status !== 'draft'}
                                    rows={6}
                                    className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-indigo-500 font-mono text-sm resize-y"
                                    placeholder="Текст сообщения"
                                />
                                {detailCampaign?.status === 'draft' && (
                                    <button
                                        onClick={saveMessageText}
                                        disabled={savingText}
                                        className="mt-2 flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50"
                                    >
                                        <Save size={16} /> {savingText ? 'Сохранение...' : 'Обновить текст'}
                                    </button>
                                )}
                            </div>

                            {/* Статистика */}
                            {statsCache[detailId] && (
                                <>
                                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                                        <div className="bg-slate-50 rounded-lg p-3">
                                            <p className="text-xs text-slate-500">Всего</p>
                                            <p className="font-bold text-slate-800">{statsCache[detailId].total_recipients}</p>
                                        </div>
                                        <div className="bg-green-50 rounded-lg p-3">
                                            <p className="text-xs text-green-700">Доставлено</p>
                                            <p className="font-bold text-green-800">{statsCache[detailId].sent}</p>
                                        </div>
                                        <div className="bg-amber-50 rounded-lg p-3">
                                            <p className="text-xs text-amber-700">Ожидает</p>
                                            <p className="font-bold text-amber-800">{statsCache[detailId].pending}</p>
                                        </div>
                                        <div className="bg-red-50 rounded-lg p-3">
                                            <p className="text-xs text-red-700">Ошибки</p>
                                            <p className="font-bold text-red-800">{statsCache[detailId].failed}</p>
                                        </div>
                                    </div>

                                    {/* Кому доставлено */}
                                    {statsCache[detailId].sent_recipients?.length > 0 && (
                                        <div>
                                            <h4 className="flex items-center gap-2 text-sm font-medium text-slate-700 mb-2">
                                                <CheckCircle size={18} className="text-green-600" /> Кому доставлено
                                            </h4>
                                            <textarea
                                                readOnly
                                                rows={4}
                                                value={statsCache[detailId].sent_recipients
                                                    .map((u) => {
                                                        const who = u.user_username ? `@${u.user_username}` : (u.tg_id ? `tg_id: ${u.tg_id}` : '—');
                                                        return `${who}${u.sent_at ? ' • ' + formatDateMoscow(u.sent_at) : ''}`;
                                                    })
                                                    .join('\n')}
                                                className="w-full px-4 py-2 border border-slate-200 rounded-lg bg-slate-50 text-sm font-mono"
                                            />
                                        </div>
                                    )}

                                    {/* Ошибки доставки */}
                                    {statsCache[detailId].failed_recipients?.length > 0 && (
                                        <div>
                                            <h4 className="flex items-center gap-2 text-sm font-medium text-red-700 mb-2">
                                                <AlertCircle size={18} /> Ошибки доставки
                                            </h4>
                                            <textarea
                                                readOnly
                                                rows={8}
                                                value={statsCache[detailId].failed_recipients
                                                    .map((u) => {
                                                        const who = u.user_username ? `@${u.user_username}` : (u.tg_id ? `tg_id: ${u.tg_id}` : '—');
                                                        return `${who}\n  ${u.error_message || '—'}`;
                                                    })
                                                    .join('\n\n')}
                                                className="w-full px-4 py-2 border border-red-200 rounded-lg bg-red-50 text-sm font-mono text-red-900"
                                            />
                                        </div>
                                    )}
                                </>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default BroadcastsPage;
